import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { ScrollText, ChevronLeft, ChevronRight, User, Users, DollarSign, Settings, FileText, Megaphone } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminAuditLog = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, page_size: 50, total_count: 0, total_pages: 0 });
  const [filters, setFilters] = useState({ action: "", entity_type: "" });

  useEffect(() => {
    fetchLogs();
  }, [pagination.page, filters]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        page_size: pagination.page_size,
        ...(filters.action && { action: filters.action }),
        ...(filters.entity_type && { entity_type: filters.entity_type })
      });
      
      const res = await axios.get(`${API}/admin/audit-logs?${params}`, { withCredentials: true });
      setLogs(res.data.logs || []);
      setPagination(prev => ({ ...prev, ...res.data.pagination }));
    } catch (err) {
      toast.error("Failed to load audit logs");
    } finally {
      setLoading(false);
    }
  };

  const actionColors = {
    create: "text-green-400 bg-green-500/10",
    update: "text-blue-400 bg-blue-500/10",
    delete: "text-red-400 bg-red-500/10",
    suspend: "text-orange-400 bg-orange-500/10",
    activate: "text-emerald-400 bg-emerald-500/10",
    bulk: "text-purple-400 bg-purple-500/10"
  };

  const getActionColor = (action) => {
    if (action.includes("create")) return actionColors.create;
    if (action.includes("update")) return actionColors.update;
    if (action.includes("delete")) return actionColors.delete;
    if (action.includes("suspend")) return actionColors.suspend;
    if (action.includes("activate")) return actionColors.activate;
    if (action.includes("bulk")) return actionColors.bulk;
    return "text-slate-400 bg-slate-500/10";
  };

  const entityIcons = {
    partner: Users,
    referral: FileText,
    commission: DollarSign,
    payouts: DollarSign,
    settings: Settings,
    announcement: Megaphone
  };

  const getEntityIcon = (entityType) => {
    return entityIcons[entityType] || User;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <ScrollText className="h-7 w-7 text-emerald-500" />
          Audit Log
        </h1>
        <p className="text-slate-400 mt-1">Track all admin actions and changes</p>
      </div>

      {/* Filters */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <Select 
              value={filters.action || "all"} 
              onValueChange={(v) => { setFilters(prev => ({ ...prev, action: v === "all" ? "" : v })); setPagination(prev => ({ ...prev, page: 1 })); }}
            >
              <SelectTrigger className="w-48 bg-slate-700 border-slate-600 text-slate-300" data-testid="action-filter">
                <SelectValue placeholder="All Actions" />
              </SelectTrigger>
              <SelectContent className="bg-slate-700 border-slate-600">
                <SelectItem value="all">All Actions</SelectItem>
                <SelectItem value="create_partner">Create Partner</SelectItem>
                <SelectItem value="update_partner">Update Partner</SelectItem>
                <SelectItem value="suspend_partner">Suspend Partner</SelectItem>
                <SelectItem value="activate_partner">Activate Partner</SelectItem>
                <SelectItem value="delete_partner">Delete Partner</SelectItem>
                <SelectItem value="update_referral">Update Referral</SelectItem>
                <SelectItem value="delete_referral">Delete Referral</SelectItem>
                <SelectItem value="mark_payout_paid">Mark Payout Paid</SelectItem>
                <SelectItem value="update_settings">Update Settings</SelectItem>
                <SelectItem value="create_announcement">Create Announcement</SelectItem>
              </SelectContent>
            </Select>

            <Select 
              value={filters.entity_type || "all"} 
              onValueChange={(v) => { setFilters(prev => ({ ...prev, entity_type: v === "all" ? "" : v })); setPagination(prev => ({ ...prev, page: 1 })); }}
            >
              <SelectTrigger className="w-48 bg-slate-700 border-slate-600 text-slate-300" data-testid="entity-filter">
                <SelectValue placeholder="All Entities" />
              </SelectTrigger>
              <SelectContent className="bg-slate-700 border-slate-600">
                <SelectItem value="all">All Entities</SelectItem>
                <SelectItem value="partner">Partners</SelectItem>
                <SelectItem value="referral">Referrals</SelectItem>
                <SelectItem value="commission">Commissions</SelectItem>
                <SelectItem value="settings">Settings</SelectItem>
                <SelectItem value="announcement">Announcements</SelectItem>
              </SelectContent>
            </Select>

            <span className="text-sm text-slate-400 ml-auto">
              {pagination.total_count} total entries
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Audit Logs Table */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
            </div>
          ) : logs.length === 0 ? (
            <p className="text-slate-400 text-center py-16">No audit logs found</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700 bg-slate-900/50">
                    <th className="text-left p-4 text-slate-400 font-medium">Timestamp</th>
                    <th className="text-left p-4 text-slate-400 font-medium">Admin</th>
                    <th className="text-left p-4 text-slate-400 font-medium">Action</th>
                    <th className="text-left p-4 text-slate-400 font-medium">Entity</th>
                    <th className="text-left p-4 text-slate-400 font-medium">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => {
                    const Icon = getEntityIcon(log.entity_type);
                    const actionColorClass = getActionColor(log.action);
                    
                    return (
                      <tr key={log.audit_id} className="border-b border-slate-700 hover:bg-slate-700/30">
                        <td className="p-4">
                          <span className="text-slate-300 text-sm">
                            {new Date(log.timestamp).toLocaleString()}
                          </span>
                        </td>
                        <td className="p-4">
                          <span className="text-white text-sm">{log.admin_email}</span>
                        </td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${actionColorClass}`}>
                            {log.action.replace(/_/g, " ").toUpperCase()}
                          </span>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <Icon className="h-4 w-4 text-slate-400" />
                            <span className="text-slate-300 text-sm capitalize">{log.entity_type}</span>
                            <span className="text-slate-500 text-xs">{log.entity_id}</span>
                          </div>
                        </td>
                        <td className="p-4">
                          <span className="text-slate-400 text-xs font-mono">
                            {JSON.stringify(log.details).substring(0, 50)}
                            {JSON.stringify(log.details).length > 50 && "..."}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {pagination.total_pages > 1 && (
            <div className="flex items-center justify-between p-4 border-t border-slate-700">
              <span className="text-sm text-slate-400">
                Page {pagination.page} of {pagination.total_pages}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page <= 1}
                  className="border-slate-600 text-slate-300"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page >= pagination.total_pages}
                  className="border-slate-600 text-slate-300"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminAuditLog;
